import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../ui/dialog";
import {
    Shield,
    Globe,
    FileText,
    Clock,
    CheckCircle2,
    Calendar,
    Key,
    Search,
    User,
} from "lucide-react";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import { Auth, WebUrlRecord } from "../WebUrls/interface";
import { Switch } from "../../ui/switch";
import { useToast } from "../../../hooks/use-toast";
import { submitWebForm } from "./Api/tenantweburlapi";

interface WebURLConnectAccessProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    tenant: string;
    webURL: WebUrlRecord;
}

export default function WebURLConnectAccess({
    open,
    onOpenChange,
    tenant,
    webURL
}: WebURLConnectAccessProps) {
    const [duration, setDuration] = useState("");
    const [scheduledStart, setScheduledStart] = useState("");
    const [scheduledEnd, setScheduledEnd] = useState("");
    const [loading, setLoading] = useState(false);
    const [accessType, setAccessType] = useState("DYNAMIC");
    const [justification, setJustification] = useState("");
    const [timeMinutes, setTimeMinutes] = useState("");
    const userData = useUserStore((state) => state.user);
    const [step, setStep] = useState<"credentials" | "request">("credentials");
    const [credentialSearch, setCredentialSearch] = useState("");
    const [credentials, setCredentials] = useState<Auth[]>([]);
    const [selectedCredential, setSelectedCredential] = useState<Auth>();
    const [scheduleAccess, setScheduleAccess] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const { toast } = useToast();

    const handleSubmit = async () => {
        try {
            setLoading(true);

            const scheduledStart = scheduleAccess && startDate ? new Date(startDate).toISOString() : null;
            const scheduledEnd = scheduleAccess && endDate ? new Date(endDate).toISOString() : null;
            const payload = {
                email: userData.email,
                accessUrl: webURL.url,
                accessType: "Credentials",
                accessCredential: webURL.auth?.[0]?.email || "none",
                accessARN: "arn",
                requestMessage: justification,
                requestStatus: "PENDING",
                selectedTime: accessType.toUpperCase() === "PERMANENT" ? 0 : scheduleAccess ? 0 : timeMinutes ? parseInt(timeMinutes) : 0,
                createdAt: new Date().toISOString(),
                privilegeLevel: accessType.toUpperCase(),
                tenantName: tenant,
                type: "webURL",
                timingMode:
                    accessType === "PERMANENT"
                        ? 0
                        : scheduleAccess
                            ? "SCHEDULED"
                            : "GENERIC",
                scheduledStart:
                    scheduleAccess ? scheduledStart : null,
                scheduledEnd:
                    scheduleAccess ? scheduledEnd : null,
            };

            try {
                await submitWebForm(payload);

                toast({
                    title: "Request connect",
                    description: `Successfully raised the request for ${webURL.url}`,
                });
            } catch (e) {

            }

            handleClose();
        } catch (err) {
            console.error("Failed to submit Web URL access request:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onOpenChange(false);
        setAccessType("DYNAMIC");
        setJustification("");
        setDuration("");
        setScheduledStart("");
        setScheduledEnd("");
    };

    const handleCancel = () => {
        onOpenChange(false);
        // Reset form
        setAccessType("DYNAMIC");
        setJustification("");
        setTimeMinutes("");
    };

    // Reset form when dialog closes
    useEffect(() => {
        if (!open) {
            setAccessType("timed");
            setJustification("");
            setTimeMinutes("");
        }
    }, [open]);

    const handleCredentialSelect = (credential: Auth) => {
        setSelectedCredential(credential);
        setStep("request");
    };

    const filteredCredentials = webURL.auth?.filter(credential =>
        credential?.email?.toLowerCase().includes(credentialSearch.toLowerCase())
    );

    useEffect(() => {
        if (accessType?.toLowerCase() === "permanent") {
            setScheduleAccess(false);
        }
    }, [accessType]);


    console.log(step)
    if (step === "credentials") {
        return (
            <Dialog open={open} onOpenChange={() => {
                setCredentials([])
                onOpenChange(false);
                handleCancel()
                onOpenChange(false)
            }} modal={true}>
                <DialogContent className="max-w-md">
                    <DialogHeader className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                                <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <DialogTitle className="text-xl font-semibold">Select Credentials</DialogTitle>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Choose the credentials to use for secure connection to <span className="font-medium text-foreground">{webURL.url}</span>
                                </p>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Search Section */}
                        <Card>
                            <CardContent className="pt-6">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Search credentials by name, type, or description..."
                                        value={credentialSearch}
                                        onChange={(e) => setCredentialSearch(e.target.value)}
                                        className="pl-10 text-sm"
                                        data-testid="input-credential-search"
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Credentials List */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Shield className="w-5 h-5" />
                                    Available Credentials ({filteredCredentials.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 max-h-80 overflow-y-auto">
                                    {filteredCredentials.map((credential) => (
                                        <div
                                            key={credential.id}
                                            className="p-4 border rounded-lg cursor-pointer hover-elevate transition-all duration-200 group"
                                            onClick={() => handleCredentialSelect(credential)}
                                            data-testid={`credential-${credential.id}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 flex items-center justify-center group-hover:scale-105 transition-transform">
                                                        <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-sm">{credential.email}</div>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {filteredCredentials.length === 0 && (
                                    <div className="text-center py-8">
                                        <Search className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                                        <p className="text-sm text-muted-foreground">
                                            No credentials found matching your search.
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Try adjusting your search criteria.
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <div className="flex justify-end gap-3 pt-4">
                            <Button variant="outline" onClick={handleCancel} data-testid="button-cancel" className="px-6">
                                Cancel
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <>

            <Dialog open={open} onOpenChange={() => {
                setCredentials([])
                onOpenChange(false);
                handleCancel()
                onOpenChange(false)
            }} modal={true}>
                <DialogContent className="max-w-xl">
                    <DialogHeader className="space-y-2">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/20 dark:to-blue-800/30 flex items-center justify-center">
                                <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <DialogTitle className="text-xl font-semibold">
                                    Request Access
                                </DialogTitle>
                                <p className="text-sm text-muted-foreground">
                                    Configure access for <span className="font-medium text-foreground">{webURL.url}</span>
                                </p>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="space-y-6 mt-4">

                        {/* Access Type */}
                        <div>
                            <Label className="text-sm font-medium mb-3 block flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                Access Type
                            </Label>
                            <RadioGroup value={accessType} onValueChange={setAccessType} className="grid grid-cols-2 gap-3">
                                <div className="relative">
                                    <RadioGroupItem value="DYNAMIC" id="DYNAMIC" className="peer sr-only" />
                                    <Label
                                        htmlFor="DYNAMIC"
                                        className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover-elevate cursor-pointer peer-data-[state=checked]:border-blue-600 peer-data-[state=checked]:bg-blue-50 dark:peer-data-[state=checked]:bg-blue-950/20 transition-all"
                                    >
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                            <span className="font-medium text-sm">Timed</span>
                                        </div>
                                    </Label>
                                </div>
                                <div className="relative">
                                    <RadioGroupItem value="permanent" id="permanent" className="peer sr-only" />
                                    <Label
                                        htmlFor="permanent"
                                        className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover-elevate cursor-pointer peer-data-[state=checked]:border-blue-600 peer-data-[state=checked]:bg-blue-50 dark:peer-data-[state=checked]:bg-blue-950/20 transition-all"
                                    >
                                        <div className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                                            <span className="font-medium text-sm">Permanent</span>
                                        </div>
                                    </Label>
                                </div>
                            </RadioGroup>
                        </div>

                        <Card>
                            <CardContent className="pt-3 space-y-3">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <Label className="text-xs text-muted-foreground">WebURL</Label>
                                        <div className="text-xs text-muted-foreground">{webURL.url}</div>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Credentials</Label>
                                        <div className="text-xs text-muted-foreground">{selectedCredential?.email}</div>
                                    </div>
                                </div>



                            </CardContent>
                        </Card>

                        {accessType === "DYNAMIC" && (
                            <div className="flex items-center justify-between">
                                <Label htmlFor="schedule-access" className="text-sm font-medium cursor-pointer">Schedule Access</Label>
                                <Switch
                                    id="schedule-access"
                                    checked={scheduleAccess}
                                    onCheckedChange={setScheduleAccess}
                                />
                            </div>
                        )}


                        <Card>
                            <CardContent className="pt-3 space-y-3">
                                <div>
                                    <Label htmlFor="justification" className="text-sm font-medium mb-2 block flex items-center gap-2">
                                        <FileText className="w-4 h-4" />
                                        Access Justification *
                                    </Label>
                                    <Textarea
                                        id="justification"
                                        value={justification}
                                        onChange={(e) => setJustification(e.target.value)}
                                        placeholder="Provide business justification for this access request..."
                                        className="min-h-[80px] resize-none"
                                        data-testid="textarea-justification"
                                    />
                                </div>

                                {accessType === "DYNAMIC" && !scheduleAccess && (
                                    <div>
                                        <Label htmlFor="time-minutes" className="text-sm font-medium mb-2 block flex items-center gap-2">
                                            <Clock className="w-4 h-4" />
                                            Duration (minutes) *
                                        </Label>
                                        <Input
                                            id="time-minutes"
                                            value={timeMinutes}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === "") {
                                                    setTimeMinutes("");
                                                    return;
                                                }
                                                if (!/^\d+$/.test(value)) return;
                                                const num = Number(value);
                                                if (num >= 1 && num <= 999) {
                                                    setTimeMinutes(value);
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (["e", "E", "+", "-", "."].includes(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            placeholder="e.g., 60"
                                            type="number"
                                            min="1"
                                            max="999"
                                            className="max-w-xs"
                                            data-testid="input-time-minutes"
                                        />
                                    </div>
                                )}

                                {scheduleAccess && (
                                    <Card>
                                        <CardContent className="pt-3 space-y-3">
                                            <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                                                <Calendar className="w-4 h-4" />
                                                Schedule Window *
                                            </Label>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <Label htmlFor="start-date" className="text-xs text-muted-foreground mb-1 block">
                                                        Start
                                                    </Label>
                                                    <Input
                                                        id="start-date"
                                                        type="datetime-local"
                                                        value={startDate}
                                                        min={(() => {
                                                            const today = new Date();
                                                            today.setHours(0, 0, 0, 0);
                                                            return today.toISOString().slice(0, 16);
                                                        })()}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            const selected = new Date(value);
                                                            setEndDate("");

                                                            const today = new Date();
                                                            today.setHours(0, 0, 0, 0);

                                                            if (selected < today) {
                                                                toast({
                                                                    title: "Start Date/Time Selection Criteria",
                                                                    description: "Start date/time cannot be before today.",
                                                                    variant: "destructive",
                                                                });
                                                                return;
                                                            }

                                                            const maxAllowed = new Date();
                                                            maxAllowed.setMonth(today.getMonth() + 3);

                                                            if (selected > maxAllowed) {
                                                                toast({
                                                                    title: "Start Date/Time Selection Criteria",
                                                                    description: "Start date/time cannot be more than 3 months from today.",
                                                                    variant: "destructive",
                                                                });
                                                                return;
                                                            }

                                                            setStartDate(value);

                                                        }}
                                                        className="appearance-auto bg-background border border-input text-foreground px-2 py-1.5 rounded-md w-full focus-visible:ring-2 focus-visible:ring-blue-500"
                                                        data-testid="input-start-date"
                                                    />
                                                </div>
                                                <div>
                                                    <Label htmlFor="end-date" className="text-xs text-muted-foreground mb-1 block">
                                                        End
                                                    </Label>
                                                    <Input
                                                        id="end-date"
                                                        type="datetime-local"
                                                        value={endDate}
                                                        disabled={!startDate}
                                                        min={startDate || new Date().toISOString().slice(0, 16)}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            const now = new Date();
                                                            const end = new Date(value);
                                                            const start = startDate ? new Date(startDate) : now;

                                                            if (end <= start) {
                                                                toast({
                                                                    title: "End Date/Time Selection Criteria",
                                                                    description: "End date/time must be later than the start date/time.",
                                                                    variant: "destructive",
                                                                });
                                                                return;
                                                            }

                                                            if (end < now) {
                                                                toast({
                                                                    title: "End Date/Time Selection Criteria",
                                                                    description: "End date/time cannot be in the past.",
                                                                    variant: "destructive",
                                                                });
                                                                return;
                                                            }

                                                            const maxAllowed = new Date(start);
                                                            maxAllowed.setDate(start.getDate() + 3);

                                                            if (end > maxAllowed) {
                                                                toast({
                                                                    title: "End Date/Time Selection Criteria",
                                                                    description: "End date/time cannot be more than 3 days after the start date/time.",
                                                                    variant: "destructive",
                                                                });
                                                                return;
                                                            }

                                                            setEndDate(value);
                                                        }}

                                                        className="appearance-auto bg-background border border-input text-foreground px-2 py-1.5 rounded-md w-full focus-visible:ring-2 focus-visible:ring-blue-500"
                                                        data-testid="input-end-date"
                                                    />
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}
                            </CardContent>
                        </Card>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3 pt-3 border-t">
                            <Button variant="outline" onClick={handleCancel} data-testid="button-cancel" size="sm">
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={
                                    !justification.trim() ||
                                    (accessType === "DYNAMIC" &&
                                        ((!scheduleAccess && !timeMinutes) ||
                                            (scheduleAccess && (!startDate || !endDate))))
                                }
                                data-testid="button-submit"
                                size="sm"
                                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                            >
                                <Shield className="w-4 h-4 mr-1" />
                                Submit
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>



        </>
    );
}
